import React, { useEffect, useState } from 'react';
import { getCompanyUsers } from '../../../services/axios/AuthApis/TeamCollabApis';
import { getModuleAssignToApi } from '../../../services/axios/AuthApis/AiLMSApis';
import { useSelector } from 'react-redux';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import toast from '../../../utils/notify';

// moduleIds: string[] (one or more LmsCustomerResponse IDs)
const AssignLMSModuleModal = ({ isOpen, onClose, moduleIds = [], moduleId, onAssign }) => {
    const resolvedIds = moduleIds.length > 0 ? moduleIds : (moduleId ? [moduleId] : []);

    const [selectedEmployees, setSelectedEmployees] = useState([]); // user_id[]
    const [selectedEmpMap, setSelectedEmpMap] = useState({});       // user_id → label string
    const [dueDate, setDueDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingEmp, setLoadingEmp] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 5;

    const userDetail = useSelector((state) => state.user);
    let customerMasterId = null;
    if (userDetail?.user?.role === 'customer_admin') {
        customerMasterId = userDetail?.user?.customer_master_id;
    } else if (userDetail?.user?.role == 'security_analyst') { 
        const selectedCompany = useSelector((state) => state.company.selectedCompany);
        customerMasterId = selectedCompany?.company_id;
    }

    const fetchEmployees = async (searchValue = '', pageNumber = 1) => {
        try {
            setLoadingEmp(true);
            const response = await getCompanyUsers(customerMasterId, pageNumber, pageSize, searchValue);
            const results = response?.results || [];
            setEmployees(results);
            setTotalPages(response?.count > 0 ? Math.ceil(response.count / pageSize) : 1);

            // Resolve names for any pre-filled IDs found in these results
            if (results.length > 0) {
                setSelectedEmpMap((prev) => {
                    const updates = {};
                    results.forEach((emp) => {
                        if (!prev[emp.user_id]) {
                            updates[emp.user_id] = `${emp.firstname || ''} ${emp.lastname || ''}`.trim() || emp.user_id;
                        }
                    });
                    return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
                });
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            setLoadingEmp(false);
        }
    };

    // Pre-fill already-assigned employees (single module)
    useEffect(() => {
        if (!isOpen || resolvedIds.length !== 1) return;
        getModuleAssignToApi(resolvedIds[0])
            .then((res) => {
                if (res?.success) {
                    // backend returns { success, employees: [...], due_date }
                    const ids = res.employees || res.data || [];
                    setSelectedEmployees(Array.isArray(ids) ? ids : []);
                    if (res.due_date) {
                        try { setDueDate(new Date(res.due_date).toISOString().split('T')[0]); } catch { /* ignore */ }
                    }
                }
            })
            .catch(() => {});
    }, [isOpen, resolvedIds.join(',')]);

    // Debounced search
    useEffect(() => {
        if (!search.trim()) { setEmployees([]); setPage(1); return; }
        const t = setTimeout(() => { setPage(1); fetchEmployees(search, 1); }, 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        if (search.trim()) fetchEmployees(search, page);
    }, [page]);

    // Use selectedEmployees as the source of truth for add/remove
    const toggleEmployee = (emp) => {
        const userId = emp.user_id;
        const name = `${emp.firstname || ''} ${emp.lastname || ''}`.trim() || userId;
        const isSelected = selectedEmployees.includes(userId);

        if (isSelected) {
            setSelectedEmployees((prev) => prev.filter((id) => id !== userId));
            setSelectedEmpMap((prev) => { const n = { ...prev }; delete n[userId]; return n; });
        } else {
            setSelectedEmployees((prev) => [...prev, userId]);
            setSelectedEmpMap((prev) => ({ ...prev, [userId]: name }));
        }
    };

    const removeEmployee = (userId) => {
        setSelectedEmployees((prev) => prev.filter((id) => id !== userId));
        setSelectedEmpMap((prev) => { const n = { ...prev }; delete n[userId]; return n; });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!dueDate) { toast.error('Please select a due date'); return; }
        if (selectedEmployees.length === 0) { toast.error('Please select at least one employee'); return; }
        setLoading(true);
        try {
            await onAssign({ module_ids: resolvedIds, employees: selectedEmployees, due_date: dueDate });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedEmployees([]);
        setSelectedEmpMap({});
        setDueDate('');
        setSearch('');
        setEmployees([]);
        setPage(1);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <div className="flex items-center gap-2">
                        <UserGroupIcon className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-gray-900">
                            Assign {resolvedIds.length > 1 ? `${resolvedIds.length} Modules` : 'Module'}
                        </h3>
                    </div>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <XMarkIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Multi-module badge */}
                {resolvedIds.length > 1 && (
                    <div className="px-5 pt-3">
                        <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                            <CheckIcon className="w-4 h-4 flex-shrink-0" />
                            <span><span className="font-semibold">{resolvedIds.length} modules</span> selected — employees and due date apply to all</span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Due Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date *</label>
                        <input
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            min={new Date().toISOString().split('T')[0]}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                        />
                    </div>

                    {/* Employee Search */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Assign to Employees *
                        </label>

                        <div className="relative mb-2">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search employees..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        {/* Selected chips — always use selectedEmpMap for name */}
                        {selectedEmployees.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {selectedEmployees.map((uid) => {
                                    const label = selectedEmpMap[uid] || uid;
                                    return (
                                        <span
                                            key={uid}
                                            style={{ backgroundColor: '#e0e7ff', color: '#1e1b4b', border: '1px solid #c7d2fe' }}
                                            className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium"
                                        >
                                            {label}
                                            <button
                                                type="button"
                                                onClick={() => removeEmployee(uid)}
                                                style={{ color: '#4338ca' }}
                                                className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-indigo-200 transition-colors leading-none"
                                            >
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Search results */}
                        {search.trim() && (
                            loadingEmp ? (
                                <div className="text-sm text-gray-500 py-2 text-center">Loading...</div>
                            ) : employees.length > 0 ? (
                                <div className="border border-gray-200 rounded-lg overflow-hidden">
                                    <div className="max-h-44 overflow-y-auto divide-y divide-gray-100">
                                        {employees.map((emp) => {
                                            const checked = selectedEmployees.includes(emp.user_id);
                                            return (
                                                <label
                                                    key={emp.employee_id || emp.user_id}
                                                    className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => toggleEmployee(emp)}
                                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    />
                                                    <span className="text-sm text-gray-800">
                                                        {emp.firstname} {emp.lastname}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-xs text-gray-500">
                                            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}
                                                className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-white transition">
                                                ← Prev
                                            </button>
                                            <span>Page {page} of {totalPages}</span>
                                            <button type="button" disabled={page === totalPages} onClick={() => setPage(page + 1)}
                                                className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-white transition">
                                                Next →
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-400 text-center py-2">No employees found</div>
                            )
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end gap-2 pt-2 border-t">
                        <button type="button" onClick={handleClose} disabled={loading}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={loading || !dueDate || selectedEmployees.length === 0}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2">
                            {loading && (
                                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                            )}
                            {loading ? 'Assigning...' : `Assign to ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AssignLMSModuleModal;
